import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import loader from '@monaco-editor/loader';

import MonacoContainer from '@monaco-editor/react/lib/es/MonacoContainer';
import useMount from '../hooks/useMount';
import useUpdate from '../hooks/useUpdate';
import usePrevious from '../hooks/usePrevious';
import { noop, getOrCreateModel, isUndefined } from './utils';

const viewStates = new Map();

const functions = [
    "Math.SQRT(",
    "Math.ABS",
    "Math.ACOS",
    "Math.ASIN",
    "Math.ATAN",
    "Math.Ceiling",
    "Math.FLOOR",
    "Math.COS",
    "Math.COSH",
    "Math.EXP",
    "Math.E",
    "Math.LOG",
    "Math.ROUND",
    "Math.SIGN",
    "Math.SIN",
    "Math.SINH",
    "Math.TAN",
    "Math.TANH",
    "Math.TRUNCATE",
    "Math.SUM",
    "Math.AVG",
    "Math.PI",
    "Math.POWER",
    "Math.MAX",
    "Math.MIN",
    "IF",
    "AND",
    "OR",
    "String.ToUpper",
    "String.ToLower",
    "String.Concat",
    "String.Trim",
    "String.TrimStart",
    "String.TrimEnd",
    "String.Left",
    "String.Right",
    "String.Substring",
    "String.Replace",
    "String.Remove",
    "String.StartsWith",
    "String.EndsWith",
    "String.IndexOf",
    "String.LastIndexOf",
    "String.Contains",
    "String.Insert",
    "String.Length",
    "String.WordWrap",
    "String.IsEmptyOrNull",
    "String.RowCount",
    "String.CountOccurences",
    "String.PadLeft",
    "String.PadRight",
    "String.FirstLine",
    "String.LastLine",
    "String.GetLineByRowNumber",
    "String.GetColumn",
    "String.Guid",
    "Date.Now",
    "Date.UtcNow",
    "Date.AddYears",
    "Date.AddMonths",
    "Date.AddDays",
    "Date.AddWorkDays",
    "Date.AddHours",
    "Date.AddMinutes",
    "Date.AddSeconds",
    "Date.GetMonthName",
    "Date.GetQuarter",
    "Date.GetWeek",
    "Date.GetWeekOfMonth",
    "Date.GetWeekDayNumber",
    "Date.IsWorkDay",
    "Date.LastWorkDay",
    "Date.LastWorkDayOfMonth",
    "Date.FirstWorkDayOfMonth",
    "Date.WorkDaysBetween",
    "Date.DaysBetween",
    ];

function Editor({
  defaultValue,
  defaultLanguage,
  defaultPath,
  value,
  language,
  path,
  /* === */
  theme,
  line,
  loading,
  options,
  overrideServices,
  saveViewState,
  keepCurrentModel,
  /* === */
  width,
  height,
  className,
  wrapperProps,
  /* === */
  beforeMount,
  onMount,
  onChange,
  onValidate,
}) {
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [isMonacoMounting, setIsMonacoMounting] = useState(true);
  const monacoRef = useRef(null);
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const onMountRef = useRef(onMount);
  const beforeMountRef = useRef(beforeMount);
  const subscriptionRef = useRef(null);
  const valueRef = useRef(value);
  const previousPath = usePrevious(path);

  const getCompletionProvider = function(monaco) {
    return {
        provideCompletionItems: function (model, position) {
            //console.log(model, position);
            // get editor content before the pointer
            //let textUntilPosition = getTextBeforePointer();
            // get content info - are we inside of the area where we don't want suggestions,
            // what is the content without those areas
            //let info = getAreaInfo(textUntilPosition); // isCompletionAvailable, clearedText
            // if we don't want any suggestions, return empty array
            // if (!info.isCompletionAvailable) {
            //     return [];
            // }
            // if we want suggestions, inside of which tag are we?
            //var lastTag = getLastOpenedTag(info.clearedText);
            // parse the content (not cleared text) into an xml document
            //var xmlDoc = stringToXml(textUntilPosition);
            // get opened tags to see what tag we should look for in the XSD schema
            //var openedTags;
            // get the elements/attributes that are already mentioned in the element we're in
            //var usedItems;
            // find the last opened tag in the schema to see what elements/attributes it can have
            //var currentItem;

            // return available elements/attributes if the tag exists in the schema or an empty
            // array if it doesn't
            var suggestions = [];
            for(var i = 0; i < functions.length; i++)
            {
              suggestions[i] = {
                  label: functions[i],
                  insertText: functions[i]+'(',
                  kind: 1,//'Function',
                  detail: 'Type',
                  //command:{id:'editor.action.triggerParameterHints'},
                  //documentation: 'getItemDocumentation(children[i])'
                }
            }
            return {suggestions};
        }
    };
    }
    const getSignatureHelpProvider = function(monaco) {
        return {
          signatureHelpTriggerCharacters: ['(', ','],
            provideSignatureHelp: function (model, position) {
                //console.log(model, position);

                var value = {
                    activeParameter: 0,
                    activeSignature: 0,
                    signatures:[{
                        documentation:'String.Pad(',
                        label:'String.Pad(',
                        activeParameter:0,
                        parameters:[
                            { 
                               documentation:'',
                                label:'string',
                            },
                            { 
                                documentation:'',
                                 label:'padWith',
                             },
                             { 
                                documentation:'',
                                 label:'totalLength',
                             },
                        ]
                    }
                  ]
                }
                return {value, dispose:()=>{}};
            }
        };
        }

  useMount(() => {
    const cancelable = loader.init();

    cancelable
      .then(monaco => ((monacoRef.current = monaco) && setIsMonacoMounting(false)))
      .catch(error => error?.type !== 'cancelation' &&
        console.error('Monaco initialization: error:', error));

    return () => editorRef.current ? disposeEditor() : cancelable.cancel();
  });

  useUpdate(() => {
    const model = getOrCreateModel(
      monacoRef.current,
      defaultValue || value,
      defaultLanguage || language,
      path,
    );

    if (model !== editorRef.current.getModel()) {
      saveViewState && viewStates.set(previousPath, editorRef.current.saveViewState());
      editorRef.current.setModel(model);
      saveViewState && editorRef.current.restoreViewState(viewStates.get(path));
    }
  }, [path], isEditorReady);

  useUpdate(() => {
    editorRef.current.updateOptions(options);
  }, [options], isEditorReady);

  useUpdate(() => {
    if (editorRef.current.getOption(monacoRef.current.editor.EditorOption.readOnly)) {
      editorRef.current.setValue(value);
    } else {
      if (value !== editorRef.current.getValue()) {
        editorRef.current.executeEdits('', [{
          range: editorRef.current.getModel().getFullModelRange(),
          text: value,
          forceMoveMarkers: true,
        }]);

        editorRef.current.pushUndoStop();
      }
    }
  }, [value], isEditorReady);

  useUpdate(() => {
    monacoRef.current.editor.setModelLanguage(editorRef.current.getModel(), language);
    
  }, [language], isEditorReady);

  useUpdate(() => {
    // reason for undefined check: https://github.com/suren-atoyan/monaco-react/pull/188
    if(!isUndefined(line)) {
      editorRef.current.revealLine(line);
    }
  }, [line], isEditorReady);

  useUpdate(() => {
    monacoRef.current.editor.setTheme(theme);
  }, [theme], isEditorReady);

  const createEditor = useCallback(() => {
    beforeMountRef.current(monacoRef.current);
    const autoCreatedModelPath = path || defaultPath;

    const defaultModel = getOrCreateModel(
      monacoRef.current,
      value || defaultValue,
      defaultLanguage || language,
      autoCreatedModelPath,
    );

    editorRef.current = monacoRef.current.editor.create(containerRef.current, {
      model: defaultModel,
      automaticLayout: true,
      ...options,
    }, overrideServices);

    monacoRef.current.languages.registerCompletionItemProvider('vb', getCompletionProvider(monacoRef.current));
    //monacoRef.current.languages.registerSignatureHelpProvider('vb', getSignatureHelpProvider(monacoRef.current));

    monacoRef.current.languages.registerHoverProvider('vb', {
      provideHover: function(model, position) { 
          // Log the current word in the console, you probably want to do something else here.
          //console.log(model.getWordAtPosition(position));
          var wordAtPos = model.getWordAtPosition(position);
          if(wordAtPos !== null){
            const {word, startColumn, endColumn} = wordAtPos;
            const functionName = model.getValueInRange({
              startLineNumber: position.lineNumber,
              startColumn: startColumn-("String.").length, 
              endLineNumber: position.lineNumber,
              endColumn: endColumn+1
            });

            if(functionName == 'String.Pad(')
            return{
              contents :[{value:'**String.Pad(string text, string padWith, int totalLength)**'},{value:'(method)\n\n monaco.editor.ITextModel.getLineCount(): number'}]
            }
        }
      }
  });

    saveViewState && editorRef.current.restoreViewState(viewStates.get(autoCreatedModelPath));

    monacoRef.current.editor.setTheme(theme);

    setIsEditorReady(true);
  }, [
    defaultValue,
    defaultLanguage,
    defaultPath,
    value,
    language,
    path,
    options,
    overrideServices,
    saveViewState,
    theme,
  ]);

  useEffect(() => {
    if (isEditorReady) {
      onMountRef.current(
        editorRef.current,
        monacoRef.current,
      );
    }
  }, [isEditorReady]);

  useEffect(() => {
    !isMonacoMounting && !isEditorReady && createEditor();
  }, [isMonacoMounting, isEditorReady, createEditor]);

  // subscription
  // to avoid unnecessary updates (attach - dispose listener) in subscription
  valueRef.current = value;

  useEffect(() => {
    if (isEditorReady && onChange) {
      subscriptionRef.current?.dispose();
      subscriptionRef.current = editorRef.current?.onDidChangeModelContent(event => {
        const editorValue = editorRef.current.getValue();

        if (valueRef.current !== editorValue) {
          onChange(editorValue, event);
        }
      });
    }
  }, [isEditorReady, onChange]);

  // onValidate
  useEffect(() => {
    if (isEditorReady) {
      const changeMarkersListener = monacoRef.current.editor.onDidChangeMarkers(uris => {
        const editorUri = editorRef.current.getModel()?.uri;

        if (editorUri) {
          const currentEditorHasMarkerChanges = uris.find((uri) => uri.path === editorUri.path);
          if (currentEditorHasMarkerChanges) {
            const markers = monacoRef.current.editor.getModelMarkers({ resource: editorUri });
            onValidate?.(markers);
          }
        }
      });
   
      return () => {
        changeMarkersListener?.dispose();
      };
    }
  }, [isEditorReady, onValidate]);

  function disposeEditor() {
    subscriptionRef.current?.dispose();

    if (keepCurrentModel) {
      saveViewState && viewStates.set(path, editorRef.current.saveViewState());
    } else {
      editorRef.current.getModel()?.dispose();
    }

    editorRef.current.dispose();
  }

  return (
    <MonacoContainer
      width={width}
      height={height}
      isEditorReady={isEditorReady}
      loading={loading}
      _ref={containerRef}
      className={className}
      wrapperProps={wrapperProps}
    />
  );
}

Editor.propTypes = {
  defaultValue: PropTypes.string,
  defaultPath: PropTypes.string,
  defaultLanguage: PropTypes.string,
  value: PropTypes.string,
  language: PropTypes.string,
  path: PropTypes.string,
  /* === */
  theme: PropTypes.string,
  line: PropTypes.number,
  loading: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  options: PropTypes.object,
  overrideServices: PropTypes.object,
  saveViewState: PropTypes.bool,
  keepCurrentModel: PropTypes.bool,
  /* === */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  wrapperProps: PropTypes.object,
  /* === */
  beforeMount: PropTypes.func,
  onMount: PropTypes.func,
  onChange: PropTypes.func,
  onValidate: PropTypes.func,
};

Editor.defaultProps = {
  theme: 'light',
  loading: 'Loading...',
  options: {},
  overrideServices: {},
  saveViewState: true,
  keepCurrentModel: false,
  /* === */
  width: '100%',
  height: '100%',
  wrapperProps: {},
  /* === */
  beforeMount: noop,
  onMount: noop,
  onValidate: noop,
};

export default Editor;